import { Box, Chip, Typography } from "@mui/material";
import MuiPagination from "@mui/material/Pagination";
import PaginationItem from "@mui/material/PaginationItem";
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SPACING } from "../constants/spacing";

const PAGE_SIZE_OPTIONS = [12, 30, 50, 100];

const Pagination = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  onPageSizeChange,
  showInfo = true,
  size = "medium",
  variant = "contained",
  shape = "rounded",
  siblingCount = 0,
  boundaryCount = 1,
}) => {
  const { t } = useTranslation("common");
  const [sizePickerVisible, setSizePickerVisible] = useState(false);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const handleChange = (event, value) => {
    onPageChange(value);
  };

  const handleSizeChange = (newSize) => {
    if (onPageSizeChange) {
      onPageSizeChange(newSize);
    }
    setSizePickerVisible(false);
  };

  const iconSize = size === "small" ? 16 : 18;

  if (totalPages <= 1 && !onPageSizeChange) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        justifyContent: "space-between",
        alignItems: "center",
        gap: 2,
        mt: SPACING.lg,
        mb: SPACING.sm,
      }}
    >
      {showInfo && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            order: { xs: 2, sm: 1 },
          }}
        >
          <Typography variant="bodyMd" color="text.secondary">
            {t("pagination.showing", {
              start: startItem,
              end: endItem,
              total: totalItems,
            })}
          </Typography>

          {onPageSizeChange && (
            <Box
              onMouseEnter={() => setSizePickerVisible(true)}
              onMouseLeave={() => setSizePickerVisible(false)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                position: "relative",
              }}
            >
              <Chip
                label={`${itemsPerPage} / ${t("pagination.page")}`}
                size="small"
                variant="outlined"
                sx={{
                  cursor: "pointer",
                  fontSize: "0.75rem",
                  height: 24,
                  opacity: 0.6,
                  transition: "opacity 0.2s ease",
                  "&:hover": { opacity: 1 },
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: "100%",
                  left: "50%",
                  mb: 0.5,
                  display: "flex",
                  gap: 0.5,
                  p: 0.5,
                  backgroundColor: "background.paper",
                  borderRadius: 1,
                  boxShadow: 3,
                  opacity: sizePickerVisible ? 1 : 0,
                  visibility: sizePickerVisible ? "visible" : "hidden",
                  transform: sizePickerVisible
                    ? "translateX(-50%) translateY(0)"
                    : "translateX(-50%) translateY(4px)",
                  transition: "all 0.2s ease",
                  zIndex: 10,
                }}
              >
                {PAGE_SIZE_OPTIONS.map((opt) => (
                  <Chip
                    key={opt}
                    label={opt}
                    size="small"
                    variant={itemsPerPage === opt ? "filled" : "outlined"}
                    color={itemsPerPage === opt ? "primary" : "default"}
                    onClick={() => handleSizeChange(opt)}
                    sx={{
                      cursor: "pointer",
                      fontSize: "0.75rem",
                      height: 24,
                      minWidth: 36,
                      transition: "all 0.15s ease",
                      "&:hover": {
                        transform: "scale(1.08)",
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            order: { xs: 1, sm: 2 },
          }}
        >
          <MuiPagination
            count={totalPages}
            page={currentPage}
            onChange={handleChange}
            size={size}
            variant={variant}
            shape={shape}
            color="primary"
            showFirstButton
            showLastButton
            siblingCount={siblingCount}
            boundaryCount={boundaryCount}
            renderItem={(item) => (
              <PaginationItem
                slots={{
                  previous: () => <ChevronLeft size={iconSize} />,
                  next: () => <ChevronRight size={iconSize} />,
                  first: () => <ChevronsLeft size={iconSize} />,
                  last: () => <ChevronsRight size={iconSize} />,
                }}
                {...item}
              />
            )}
            sx={{
              "& .MuiPaginationItem-root": {
                fontSize: size === "small" ? "0.875rem" : "0.9375rem",
                fontWeight: 500,
                minWidth: size === "small" ? 32 : 40,
                height: size === "small" ? 32 : 40,
                margin: "0 2px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
              "& .MuiPaginationItem-root.Mui-selected": {
                backgroundColor: "primary.main",
                color: "primary.contrastText",
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
              },
              "& .MuiPaginationItem-outlined": {},
              "& .MuiPaginationItem-ellipsis": {
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                fontSize: 0,
                "&::before": {
                  content: '"⋯"',
                  color: "text.secondary",
                  fontSize: size === "small" ? "1rem" : "1.2rem",
                  lineHeight: 1,
                },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default Pagination;
